import { useState } from "react";
import { useReducedMotion } from "framer-motion";

const TONES = {
  wedding: "bg-[#8E4A54] text-[#FFF4F0] border-[#E8B4A8]/40",
  gift: "bg-[#C24E22] text-[#FFF4EE] border-[#FFB894]/40",
  umkm: "bg-[#005C53] text-[#E9FFFB] border-[#2AA79B]/40",
  masjid: "bg-[#0B6E4F] text-[#F0FFF7] border-[#C9A227]/50",
  influencer: "bg-[#5B21B6] text-[#F5EEFF] border-[#FF3D8A]/40",
  gold: "bg-[#C9A227] text-[#141318] border-[#E8C98A]/60",
  ghost: "bg-transparent text-[#F6F0E6] border-[#F6F0E6]/30 hover:border-[#F6F0E6]/70",
};

const CONFETTI = ["#FF9A76", "#FFC94D", "#E8B4A8", "#FFFFFF", "#C9A227"];

const BloomStar = ({ className }) => (
  <svg
    aria-hidden
    className={`bloom-star ${className}`}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z"
      stroke="#E8C98A"
      strokeWidth="1.4"
    />
  </svg>
);

export default function CelebrationButton({
  variant = "gold",
  href,
  onClick,
  children,
  className = "",
  testId,
  ...rest
}) {
  const reduced = useReducedMotion();
  const [pieces, setPieces] = useState([]);
  const [ink, setInk] = useState({ x: "50%", y: "50%" });
  const Comp = href ? "a" : "button";
  const external = href?.startsWith("http");

  const burst = () => {
    if (reduced || variant !== "gift") return;
    const p = Array.from({ length: 12 }, (_, i) => ({
      id: `${Date.now()}-${i}`,
      x: (Math.random() - 0.5) * 150,
      y: -20 - Math.random() * 80,
      r: (Math.random() - 0.5) * 360,
      c: CONFETTI[i % CONFETTI.length],
    }));
    setPieces(p);
    setTimeout(() => setPieces([]), 900);
  };

  const handleMove = (e) => {
    if (variant !== "umkm") return;
    const r = e.currentTarget.getBoundingClientRect();
    setInk({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  return (
    <Comp
      data-testid={testId}
      href={href}
      onClick={onClick}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      onMouseEnter={burst}
      onMouseMove={handleMove}
      className={`group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full border px-6 py-3 text-sm font-semibold tracking-wide transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 active:translate-y-0 btn-${variant} ${TONES[variant]} ${className}`}
      {...rest}
    >
      {variant === "wedding" && <span aria-hidden className="silk-overlay" />}
      {variant === "umkm" && (
        <span
          aria-hidden
          className="ink-splash"
          style={{ left: ink.x, top: ink.y }}
        />
      )}
      {variant === "masjid" && (
        <>
          <BloomStar className="left-3 top-1/2 -translate-y-1/2" />
          <BloomStar className="right-3 top-1/2 -translate-y-1/2 -scale-x-100" />
        </>
      )}
      {variant === "gift" &&
        pieces.map((p) => (
          <span
            key={p.id}
            aria-hidden
            className="confetti-piece"
            style={{
              "--cx": `${p.x}px`,
              "--cy": `${p.y}px`,
              "--cr": `${p.r}deg`,
              background: p.c,
            }}
          />
        ))}
      {variant === "influencer" ? (
        <span
          className="glitch"
          data-text={typeof children === "string" ? children : undefined}
        >
          {children}
        </span>
      ) : (
        <span className="relative z-10 inline-flex items-center gap-2">
          {children}
        </span>
      )}
    </Comp>
  );
}
